import { fitText } from "@/lib/fit-title";
import { QaDeepDive } from "@/components/qa/QaDeepDive";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { absoluteUrl } from "@/config/sites";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { IMMIGRATION_QA_ALL, IMMIGRATION_QA_BY_SLUG, IMMIGRATION_REVIEWED_ISO, IMMIGRATION_REVIEWED_TH } from "@/data/immigration/questions";
import { JsonLd } from "@/components/JsonLd";

export const Route = createFileRoute("/immigration/q/$slug")({
  loader: ({ params }) => { if (!IMMIGRATION_QA_BY_SLUG[params.slug]) throw notFound(); return { slug: params.slug }; },
  head: ({ loaderData }) => {
    const qa = loaderData ? IMMIGRATION_QA_BY_SLUG[loaderData.slug] : undefined;
    if (!qa) return { meta: [{ title: ctrTitle("ไม่พบหน้า") }, { name: "robots", content: "noindex" }] };
    const url = absoluteUrl(`/immigration/q/${qa.slug}`);
    const title = fitText(`${qa.q} | Thai Notary Law`, 60);
    const desc = fitText(qa.a, 158);
    return { meta: [
      { title: ctrTitle(title) }, { name: "description", content: ctrDesc(desc) },
      { property: "og:title", content: ctrTitle(title) }, { property: "og:description", content: ctrDesc(desc) },
      { property: "og:url", content: url }, { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ], links: [{ rel: "canonical", href: url }] };
  },
  notFoundComponent: () => <div className="p-8 text-center">ไม่พบคำถามนี้</div>,
  errorComponent: () => <div className="p-8 text-center text-sm">โหลดหน้านี้ไม่สำเร็จ กรุณารีเฟรชอีกครั้ง</div>,
  component: ImmigrationQAPage,
});

function ImmigrationQAPage() {
  const { slug } = Route.useLoaderData();
  const qa = IMMIGRATION_QA_BY_SLUG[slug];
  if (!qa) throw notFound();
  const url = absoluteUrl(`/immigration/q/${qa.slug}`);
  const related = IMMIGRATION_QA_ALL.filter((x) => x.slug !== qa.slug && x.serviceSlug === qa.serviceSlug).slice(0, 8);
  const graph = { "@context": "https://schema.org", "@graph": [
    { "@type": "FAQPage", url, dateModified: IMMIGRATION_REVIEWED_ISO, mainEntity: [{ "@type": "Question", name: qa.q, acceptedAnswer: { "@type": "Answer", text: qa.a } }] },
    { "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "หน้าแรก", item: absoluteUrl("/") },
      { "@type": "ListItem", position: 2, name: "งานตรวจคนเข้าเมือง", item: absoluteUrl("/immigration") },
      { "@type": "ListItem", position: 3, name: "คำถาม", item: absoluteUrl("/immigration/questions") },
      { "@type": "ListItem", position: 4, name: qa.q, item: url },
    ] },
  ] };
  return <>
    <JsonLd data={graph} />
    <Breadcrumbs items={[{ label: "หน้าแรก", to: "/" }, { label: "งานตรวจคนเข้าเมือง", to: "/immigration" }, { label: "คำถาม", to: "/immigration/questions" }, { label: qa.q }]} />
    <section className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="mb-4 text-2xl font-bold">{qa.q}</h1>
      <p className="answer-summary faq-answer mb-6 leading-relaxed" data-speakable>{qa.a}</p>
      <QaDeepDive path={`/immigration/q/${qa.slug}`} />
      <p className="mb-6 text-xs text-muted-foreground">คำค้นที่เกี่ยวข้อง: {qa.synonyms.join(" · ")} · ข้อมูล ณ {IMMIGRATION_REVIEWED_TH}</p>
      {related.length > 0 && <><h2 className="mb-2 text-lg font-semibold">คำถามที่เกี่ยวข้อง</h2><ul className="mb-6 list-disc space-y-1 pl-5 text-sm">{related.map((r) => <li key={r.slug}><Link to="/immigration/q/$slug" params={{ slug: r.slug }} className="text-blue-700 hover:underline">{r.q}</Link></li>)}</ul></>}
      <Link to="/contact" className="text-sm text-blue-700 hover:underline">สอบถามทางโทรศัพท์ / LINE / อีเมล →</Link>
    </section>
  </>;
}
